import React from 'react';
import { Link } from 'react-router-dom';

const TABLE_ROWS = [
  ['Sedentary (RDA minimum)', '0.8', '0.36', '65 g'],
  ['General fitness', '1.2–1.6', '0.55–0.7', '100–130 g'],
  ['Muscle gain', '1.6–2.2', '0.7–1.0', '130–180 g'],
  ['Fat loss (preserve muscle)', '2.0–2.4', '0.9–1.1', '160–195 g'],
  ['Endurance / HYROX athletes', '1.4–1.8', '0.65–0.8', '115–145 g'],
  ['Athletes 40+ (anabolic resistance)', '1.8–2.2', '0.8–1.0', '145–180 g'],
];

const RESEARCH = [
  {
    bold: 'The 1.6 g/kg plateau.',
    text: 'A 2018 meta-analysis of 49 randomized trials (1,863 participants) found protein supplementation significantly increased strength and lean mass gains from resistance training, with benefits plateauing around 1.6 g/kg/day and an upper confidence limit of 2.2 g/kg. Morton et al., British Journal of Sports Medicine, 2018.',
    pmid: '28698222',
  },
  {
    bold: 'ISSN Position Stand.',
    text: 'The International Society of Sports Nutrition recommends 1.4 to 2.0 g/kg/day for building and maintaining muscle in exercising individuals. Jäger et al., JISSN, 2017.',
    pmid: '28642676',
  },
  {
    bold: 'Higher protein protects muscle in a deficit.',
    text: 'In a controlled trial, subjects eating 2.4 g/kg during a 40% calorie deficit gained lean mass while losing fat; the 1.2 g/kg group only maintained. Longland et al., AJCN, 2016.',
    pmid: '26817506',
  },
  {
    bold: 'Healthy kidneys are not harmed by high protein.',
    text: 'A 2018 meta-analysis of 28 trials found protein intakes above 1.5 g/kg/day did not adversely affect kidney function in healthy adults. Devries et al., Journal of Nutrition, 2018.',
    pmid: '30383278',
  },
];

export const ProteinGuideContent = () => (
  <section id="protein-guide" className="max-w-3xl mx-auto px-4 mt-16" data-testid="protein-guide-content">
    <h2 className="text-white text-2xl font-heading mb-4">How Much Protein Do You Need Per Day?</h2>

    <p className="text-zinc-300 leading-relaxed mb-4 direct-answer" data-speakable="true">
      <strong className="text-white">Most active adults need 1.6 to 2.2 grams of protein per kilogram of body weight per day (0.7 to 1.0 g per pound).</strong>{' '}
      That is roughly double the RDA of 0.8 g/kg, which is a minimum to prevent deficiency, not a target for performance. Athletes in a fat-loss phase should aim for the higher end (2.0 to 2.4 g/kg) to preserve lean mass in a calorie deficit.
    </p>

    <p className="text-zinc-400 leading-relaxed mb-8">
      Example: a 180 lb (82 kg) lifter aiming to build muscle needs roughly 130 to 180 g of protein per day. The calculator above gives you your exact number based on body weight, goal, and training volume.
    </p>

    <h2 className="text-white text-2xl font-heading mb-4">Daily Protein Targets by Goal</h2>

    <div className="overflow-x-auto mb-8">
      <table className="w-full text-sm border-collapse" data-testid="protein-targets-table">
        <thead>
          <tr className="border-b-2 border-zinc-600 text-left">
            <th className="py-2.5 px-2 text-white font-heading font-normal uppercase tracking-wider text-xs">Goal</th>
            <th className="py-2.5 px-2 text-white font-heading font-normal uppercase tracking-wider text-xs">g per kg</th>
            <th className="py-2.5 px-2 text-white font-heading font-normal uppercase tracking-wider text-xs">g per lb</th>
            <th className="py-2.5 px-2 text-white font-heading font-normal uppercase tracking-wider text-xs">180 lb example</th>
          </tr>
        </thead>
        <tbody>
          {TABLE_ROWS.map((row) => (
            <tr key={row[0]} className="border-b border-zinc-800">
              {row.map((cell, ci) => (
                <td key={ci} className={`py-2.5 px-2 ${ci === 0 ? 'text-zinc-300' : 'text-zinc-400'}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 className="text-white text-2xl font-heading mb-4">What the Research Actually Says</h2>

    <p className="text-zinc-400 leading-relaxed mb-4">These targets are not gym-bro folklore. They come from the strongest evidence available on protein and training:</p>

    <ul className="space-y-4 mb-8">
      {RESEARCH.map((r) => (
        <li key={r.pmid} className="text-zinc-400 text-sm leading-relaxed flex gap-2">
          <span className="text-[#E63946] mt-0.5 shrink-0">→</span>
          <span>
            <strong className="text-zinc-200">{r.bold}</strong> {r.text}{' '}
            <a href={`https://pubmed.ncbi.nlm.nih.gov/${r.pmid}/`} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline">PubMed {r.pmid}</a>
          </span>
        </li>
      ))}
    </ul>

    <h2 className="text-white text-2xl font-heading mb-4">Per-Meal Protein: Does Distribution Matter?</h2>

    <p className="text-zinc-400 leading-relaxed mb-8">
      Total daily intake matters most, but distribution helps. Research on maximizing muscle protein synthesis suggests roughly{' '}
      <strong className="text-zinc-200">0.4 g/kg per meal across about 4 meals</strong>{' '}
      (Schoenfeld &amp; Aragon, JISSN, 2018.{' '}
      <a href="https://pubmed.ncbi.nlm.nih.gov/29497353/" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline">PubMed 29497353</a>).
      For a 180 lb athlete, that is about 33 g of protein per meal, four times per day. Older athletes need more per sitting: around 0.40 g/kg per meal versus 0.24 g/kg in younger adults to trigger the same synthesis response (Moore et al., J Gerontol, 2015.{' '}
      <a href="https://pubmed.ncbi.nlm.nih.gov/25056502/" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline">PubMed 25056502</a>).
    </p>

    <p className="text-zinc-500 text-xs leading-relaxed border-t border-zinc-800 pt-4">
      This calculator and guide are for educational purposes only and are not medical or dietetic advice. Protein needs vary with health conditions; consult a physician or registered dietitian before making major dietary changes, especially with kidney or liver conditions. See our{' '}
      <Link to="/editorial-policy" className="text-[#00F0FF] hover:underline">Editorial Policy</Link> for how we source research.
    </p>
  </section>
);
